import { NavLink, Outlet, useNavigate } from 'react-router-dom'

import { useSession } from '../lib/auth'
import { useModules, type NomModule } from '../lib/modules'
import { initiales } from '../lib/formatters'
import { Bouton, Pastille } from './ui/Base'
import { libelleRole } from '../lib/roles'

interface Entree {
  vers: string
  libelle: string
  /** Absente du menu — et non grisée — si le rôle ne convient pas. */
  titulaireSeulement?: boolean
  /**
   * Absente aussi tant que le serveur n'expose pas ce module.
   *
   * Le lot F a dessiné l'interface entière contre des routes que les lots C, D
   * et E écriront plus tard. Sans ce filtre, la moitié du menu mène à un encadré
   * rouge « la demande a été refusée » — ce qui se lit comme un logiciel cassé,
   * pas comme une fonction à venir.
   */
  module?: NomModule
}

const ENTREES: Entree[] = [
  { vers: '/planning', libelle: 'Planning', module: 'planning' },
  { vers: '/mon-planning', libelle: 'Mon planning', module: 'planning' },
  { vers: '/patients', libelle: 'Patients' },
  { vers: '/disponibilites', libelle: 'Disponibilités', module: 'disponibilites' },
  { vers: '/tournee', libelle: 'Ma tournée', module: 'tournees' },
  { vers: '/facturation', libelle: 'Facturation', module: 'facturation' },
  { vers: '/utilisateurs', libelle: 'Infirmiers', titulaireSeulement: true },
  // Le référentiel des actes précède le cabinet dans le menu parce qu'il le
  // précède dans l'usage : sans acte déclaré, aucune ordonnance n'est saisissable.
  { vers: '/types-soins', libelle: 'Types de soins', titulaireSeulement: true },
  { vers: '/cabinet', libelle: 'Cabinet', titulaireSeulement: true },
  { vers: '/journal-audit', libelle: "Journal d'audit", titulaireSeulement: true },
]

export function Disposition() {
  const { utilisateur, deconnecter, estTitulaire } = useSession()
  const { disponible } = useModules()
  const naviguer = useNavigate()

  // L'entrée n'apparaît pas pour un collaborateur ou un remplaçant : elle est
  // absente, pas grisée. Une entrée grisée annonce une fonction inaccessible et
  // invite à demander pourquoi ; une entrée absente ne pose pas la question.
  const entrees = ENTREES.filter(
    (e) => (!e.titulaireSeulement || estTitulaire) && (!e.module || disponible(e.module)),
  )

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:shadow"
      >
        Aller au contenu
      </a>

      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-[110rem] items-center gap-6 px-4 py-3">
          <span className="text-lg font-semibold tracking-tight text-slate-900">SoinsLib</span>

          <nav aria-label="Navigation principale" className="flex-1 overflow-x-auto">
            <ul className="flex items-center gap-1">
              {entrees.map((e) => (
                <li key={e.vers}>
                  <NavLink
                    to={e.vers}
                    className={({ isActive }) =>
                      `block whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition ${
                        isActive
                          ? 'bg-sky-50 text-sky-700'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`
                    }
                  >
                    {e.libelle}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {utilisateur ? (
            <div className="flex items-center gap-3">
              <div className="hidden text-right sm:block">
                <p className="text-sm font-medium text-slate-800">
                  {utilisateur.prenom} {utilisateur.nom}
                </p>
                <p className="flex items-center justify-end gap-1.5 text-xs text-slate-500">
                  <Pastille couleur={utilisateur.couleur} />
                  {utilisateur.titulaire_principal
                    ? 'titulaire principal'
                    : libelleRole(utilisateur.role)}
                </p>
              </div>
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold text-white"
                style={{ backgroundColor: utilisateur.couleur }}
                aria-hidden="true"
              >
                {initiales(utilisateur)}
              </span>
              <Bouton
                variante="secondaire"
                onClick={async () => {
                  await deconnecter()
                  naviguer('/connexion')
                }}
              >
                Déconnexion
              </Bouton>
            </div>
          ) : null}
        </div>
      </header>

      <main id="contenu" className="mx-auto w-full max-w-[110rem] flex-1 px-4 py-6">
        <Outlet />
      </main>
    </div>
  )
}
